/**
 * System Fields Configuration for Masters Import
 *
 * This file provides field definitions for the Masters Import Wizard UI.
 * It uses the shared propertyMapper as the single source of truth for
 * available properties, then adds UI-specific metadata (required, critical flags).
 */

import { getAvailableProperties, getAllUniqueProperties } from '@/utils/tagPropertyMapper';

/**
 * Common system fields shared across all tag types.
 * Used when displaying tags without knowing their specific type (e.g., BatchTagsDialog).
 * Generated from the shared propertyMapper to stay in sync.
 */
export const COMMON_SYSTEM_FIELDS = getAllUniqueProperties().map(prop => ({
  key: prop.property,
  displayName: prop.title,
  required: prop.property === 'name',
}));

/**
 * Critical fields by tag type - fields that are especially important for data integrity
 */
const CRITICAL_FIELDS_BY_TYPE = {
  Equipment: ['name', 'equipmentDescription', 'manufacturer'],
  Pipeline: ['name', 'lineNo', 'from', 'to'],
  Structural: ['name', 'description', 'type'],
};

/**
 * Check if a property is critical for a given tag type
 * @param {string} property - The property name (camelCase)
 * @param {string} tagType - The tag type
 * @returns {boolean}
 */
function isCriticalField(property, tagType) {
  const criticalFields = CRITICAL_FIELDS_BY_TYPE[tagType] || [];
  return criticalFields.includes(property);
}

/**
 * Get system fields available for a specific tag type.
 * Uses the shared propertyMapper as the source of truth.
 *
 * @param {string} tagType - 'Equipment' | 'Pipeline' | 'Structural'
 * @returns {Array} - Array of field definitions with { key, displayName, required, critical }
 */
export function getFieldsForTagType(tagType) {
  const properties = getAvailableProperties(tagType);

  return properties.map(prop => ({
    key: prop.property,           // camelCase property name (e.g., 'name', 'manufacturer')
    displayName: prop.title,      // Display title (e.g., 'Tag No.*', 'Manufacturer')
    required: prop.property === 'name',  // Only 'name' is required
    critical: isCriticalField(prop.property, tagType),
  }));
}

/**
 * Get only required fields for validation.
 * @param {string} tagType - The tag type
 * @returns {Array} - Array of required field definitions
 */
export function getRequiredFields(tagType) {
  return getFieldsForTagType(tagType).filter(f => f.required);
}

/**
 * Get only critical fields for highlighting in UI.
 * @param {string} tagType - The tag type
 * @returns {Array} - Array of critical field definitions
 */
export function getCriticalFields(tagType) {
  return getFieldsForTagType(tagType).filter(f => f.critical);
}

// Tag type options for dropdown
export const TAG_TYPE_OPTIONS = [
  { text: 'Equipment', value: 'Equipment', icon: 'mdi-wrench', color: 'primary' },
  { text: 'Pipeline', value: 'Pipeline', icon: 'mdi-pipe', color: 'blue' },
  { text: 'Structural', value: 'Structural', icon: 'mdi-home-city', color: 'purple' },
];

// Scope type options
export const SCOPE_TYPE_OPTIONS = [
  { text: 'Facility', value: 'Facility', icon: 'mdi-domain', description: 'Import to a single facility' },
  { text: 'Group', value: 'Group', icon: 'mdi-folder-multiple', description: 'Share across facility group' },
];

// Data type options for custom attributes
export const DATA_TYPE_OPTIONS = [
  { text: 'Text', value: 'text', icon: 'mdi-format-text' },
  { text: 'Number', value: 'number', icon: 'mdi-numeric' },
  { text: 'Date', value: 'date', icon: 'mdi-calendar' },
  { text: 'Boolean', value: 'boolean', icon: 'mdi-toggle-switch' },
];

// Duplicate handling options
export const DUPLICATE_HANDLING_OPTIONS = [
  { text: 'Skip Duplicates', value: 'Skip', description: 'Import only new tags, skip existing ones' },
  { text: 'Merge/Update', value: 'Merge', description: 'Update existing tags with new data' },
];

export default {
  TAG_TYPE_OPTIONS,
  SCOPE_TYPE_OPTIONS,
  DATA_TYPE_OPTIONS,
  DUPLICATE_HANDLING_OPTIONS,
  COMMON_SYSTEM_FIELDS,
  getFieldsForTagType,
  getRequiredFields,
  getCriticalFields,
};
